import { useCallback, useEffect, useRef, useState } from 'react';
import { TabData } from '../types';

interface MinimizedModal {
    id: number;
    name: string;
    formData: any;
}

export interface UseMinimizedModalsReturn {
    minimizedModals: MinimizedModal[];
    setMinimizedModals: React.Dispatch<React.SetStateAction<MinimizedModal[]>>;
    handleMinimizeModal: (selectedServiceTypeId: number, formRef: any, serviceName?: string, tabKey?: string, tabs?: TabData[], setTabs?: React.Dispatch<React.SetStateAction<TabData[]>>) => void;
    handleDeleteMinimizedModal: (id: number, tabKey?: string, tabs?: TabData[], setTabs?: React.Dispatch<React.SetStateAction<TabData[]>>) => void;
    handleRestoreModal: (id: number, tabKey?: string, tabs?: TabData[], setTabs?: React.Dispatch<React.SetStateAction<TabData[]>>, formRef?: any) => MinimizedModal | null;
    initializeMinimizedModals: () => void;
    syncWithSessionStorage: (forceRefresh?: boolean) => void;
}

let globalSyncInProgress = false;
let lastSyncTime = 0;
const SYNC_COOLDOWN = 2000; // 2 seconds between syncs

export const useMinimizedModals = (
    currentSessionId?: string | number,
    saveSessionTabs?: (sessionData: any) => void,
    isCallSelected?: boolean,
    isChatSelected?: boolean
): UseMinimizedModalsReturn => {
    const [minimizedModals, setMinimizedModals] = useState<MinimizedModal[]>([]);
    const lastSyncTimeRef = useRef<number>(0);
    const syncInProgressRef = useRef<boolean>(false);
    const initializedRef = useRef<boolean>(false);
    const deleteInProgressRef = useRef<boolean>(false);
    const eventListenerAddedRef = useRef<boolean>(false);
    const lastSessionIdRef = useRef<string | number | undefined>(undefined);
    const suppressRefetchRef = useRef<boolean>(false); // ✅ ADD: New ref to prevent unnecessary refetches

    // ✅ MODIFIED: NEW ref to track call/chat selection state to prevent loops
    const prevCallChatStateRef = useRef<{ isCall?: boolean, isChat?: boolean }>({});

    // ✅ ADD: New effect to listen for minimizedModalsCleared event
    useEffect(() => {
        const handleMinimizedModalsCleared = (event: CustomEvent<{ sessionId: string | number }>) => {
            // Only clear if it's for the current session
            if (event.detail.sessionId === currentSessionId) {

                // Force clear all modal state
                setMinimizedModals([]);

                // Reset all refs
                initializedRef.current = false;
                syncInProgressRef.current = false;
                deleteInProgressRef.current = false;
                suppressRefetchRef.current = false;

                // Clear any sessionStorage items for this session
                if (currentSessionId) {
                    sessionStorage.removeItem(`modal-init-${currentSessionId}`);
                    sessionStorage.removeItem(`modal-sync-${currentSessionId}`);
                    sessionStorage.removeItem(`lookup-form-init-${currentSessionId}`);
                    sessionStorage.removeItem(`chat-modal-init-${currentSessionId}`);
                }

            }
        };

        window.addEventListener('minimizedModalsCleared', handleMinimizedModalsCleared as EventListener);

        return () => {
            window.removeEventListener('minimizedModalsCleared', handleMinimizedModalsCleared as EventListener);
        };
    }, [currentSessionId]);

    // Get the current active session ID
    const getActiveSessionId = useCallback((): string | number | undefined => {
        // Use provided currentSessionId first
        if (currentSessionId) {
            return currentSessionId;
        }

        let sessionId: string | number | undefined = undefined;

        // Try to get from localStorage if not provided
        const currentCallData = localStorage.getItem('currentCallDialingEvent');
        const chatSessionData = localStorage.getItem('omnichannel_chat_session');

        // Prioritize based on call/chat selection state
        if (isCallSelected && currentCallData) {
            try {
                const callData = JSON.parse(currentCallData);
                sessionId = callData.parentCallUuid || callData.callUuid;
            } catch (error) {
                console.error('Error parsing call data:', error);
            }
        } else if (isChatSelected && chatSessionData) {
            try {
                const { chatSessionId } = JSON.parse(chatSessionData);
                sessionId = chatSessionId;
            } catch (error) {
                console.error('Error parsing chat data:', error);
            }
        }

        return sessionId;
    }, [currentSessionId, isCallSelected, isChatSelected]);

    // Enhanced method to synchronize with session storage - PRESERVE FORMDATA
    const syncWithSessionStorage = useCallback((forceRefresh = false) => {
        const initKey = `modal-sync-${currentSessionId}`;
        const lastSyncTime = parseInt(sessionStorage.getItem(initKey) || '0', 10);
        const now = Date.now();

        if (!forceRefresh && now - lastSyncTime < 200) { // ✅ REDUCED: From 500ms to 200ms
            return;
        }

        sessionStorage.setItem(initKey, now.toString());

        if (suppressRefetchRef.current && !forceRefresh) {
            return;
        }

        if (syncInProgressRef.current || (now - lastSyncTimeRef.current < 100 && !forceRefresh)) { // ✅ REDUCED: From 300ms to 100ms
            return;
        }

        try {
            syncInProgressRef.current = true;
            lastSyncTimeRef.current = now;

            const activeSessionId = getActiveSessionId();
            if (!activeSessionId) {
                syncInProgressRef.current = false;
                return;
            }


            const storedSessions = localStorage.getItem('customerSupportSession');
            if (!storedSessions) {
                syncInProgressRef.current = false;
                return;
            }

            const sessions = JSON.parse(storedSessions);
            const currentSession = sessions.find((s: any) =>
                (typeof activeSessionId === 'string' && s.callId === activeSessionId) ||
                (typeof activeSessionId === 'number' && s.chatSessionId === activeSessionId)
            );

            if (!currentSession?.searchData) {
                syncInProgressRef.current = false;
                return;
            }

            // ✅ ENHANCED: Look through ALL tabs for minimized modals with formData preservation
            const allMinimizedModals: MinimizedModal[] = [];

            currentSession.searchData.forEach((tab: any) => {
                if (tab.minimizedModals?.length > 0 && tab.formData) {
                    tab.minimizedModals.forEach((modalId: number) => {
                        const modalData = tab.formData[modalId];
                        if (modalData) {
                            // ✅ CRITICAL: Preserve all formData
                            allMinimizedModals.push({
                                id: modalId,
                                name: modalData.serviceTypeName || `Service ${modalId}`,
                                formData: modalData // Keep all form data intact
                            });
                        }
                    });
                }
            });

            // Deduplicate by ID
            const uniqueModals = allMinimizedModals.reduce((unique: MinimizedModal[], modal) => {
                const exists = unique.some(m => m.id === modal.id);
                if (!exists) {
                    unique.push(modal);
                }
                return unique;
            }, []);


            // Only update state if different
            const currentIds = minimizedModals.map(m => m.id).sort().join(',');
            const newIds = uniqueModals.map(m => m.id).sort().join(',');

            if (currentIds !== newIds || forceRefresh) {
                setMinimizedModals(uniqueModals);
            }
        } catch (error) {
            console.error('Error syncing with session storage:', error);
        } finally {
            setTimeout(() => {
                syncInProgressRef.current = false;
            }, 50); // ✅ REDUCED: From 100ms to 50ms
        }
    }, [getActiveSessionId, isCallSelected, isChatSelected, minimizedModals, currentSessionId]);

    const initializeMinimizedModals = useCallback(() => {
        // ✅ CRITICAL FIX: Add a check to prevent infinite initialization
        const currentTimestamp = Date.now();
        const lastInitTime = parseInt(sessionStorage.getItem(`modal-init-${currentSessionId}`) || '0', 10);

        // Only allow initialization once per second for the same session
        if (currentTimestamp - lastInitTime < 500) { // ✅ REDUCED: From 1000ms to 500ms
            return;
        }

        // Record this initialization timestamp
        sessionStorage.setItem(`modal-init-${currentSessionId}`, currentTimestamp.toString());


        // ✅ ADD: Disable suppression when explicitly initializing
        suppressRefetchRef.current = false;
        syncWithSessionStorage(true); // Force refresh on initial load
    }, [syncWithSessionStorage, currentSessionId]);

    // CRITICAL FIX: Move useEffect after syncWithSessionStorage is defined
    useEffect(() => {
        if (currentSessionId !== lastSessionIdRef.current) {

            // ✅ ADD: Don't clear modals immediately on session change, wait for sync
            // This helps prevent flickering when switching between sessions
            // setMinimizedModals([]);

            // Reset initialization flag
            initializedRef.current = false;

            // Update reference
            lastSessionIdRef.current = currentSessionId;

            // Initialize for new session if we have a valid ID
            if (currentSessionId) {
                // ✅ ADD: Suppress immediate refetch then force one after delay
                suppressRefetchRef.current = true;

                setTimeout(() => {
                    suppressRefetchRef.current = false;
                    syncWithSessionStorage(true);
                }, 50); // ✅ REDUCED: From 100ms to 50ms
            }
        }
    }, [currentSessionId, syncWithSessionStorage]); // FIX: Now syncWithSessionStorage is defined

    // ✅ MODIFIED: Only run initialization once on mount, sync on any changes to isCallSelected/isChatSelected
    useEffect(() => {
        // Initialize on first mount
        if (!initializedRef.current && currentSessionId) {
            initializedRef.current = true;

            setTimeout(() => {
                suppressRefetchRef.current = false;
                syncWithSessionStorage(true);
            }, 100); // ✅ REDUCED: From 200ms to 100ms
        }
    }, [currentSessionId, syncWithSessionStorage]);

    // ✅ MODIFIED: New effect with safeguards against re-triggering for same call/chat state
    useEffect(() => {
        // Skip if no session ID
        if (!currentSessionId) return;

        // CRITICAL: Check if call/chat state actually changed to prevent loops
        const callStateChanged = prevCallChatStateRef.current.isCall !== isCallSelected;
        const chatStateChanged = prevCallChatStateRef.current.isChat !== isChatSelected;

        // Only trigger if we're already initialized AND something actually changed
        if (initializedRef.current && (callStateChanged || chatStateChanged)) {

            // UPDATE: Store current state to compare against next time
            prevCallChatStateRef.current = {
                isCall: isCallSelected,
                isChat: isChatSelected
            };

            // CRITICAL: If placeholder-call, add extra debounce to prevent infinite loops
            if (currentSessionId === 'placeholder-call') {

                // Record timestamp for this sync
                const syncTimestamp = Date.now();
                lastSyncTimeRef.current = syncTimestamp;

                suppressRefetchRef.current = true;

                // Use setTimeout with check to ensure only the LAST update runs
                setTimeout(() => {
                    // Only proceed if this was the last sync request
                    if (lastSyncTimeRef.current === syncTimestamp) {
                        suppressRefetchRef.current = false;
                        // Final safeguard - only sync if it's been 3 seconds since last sync
                        if (Date.now() - syncTimestamp >= 1500) { // ✅ REDUCED: From 3000ms to 1500ms
                            syncWithSessionStorage(true);
                        } else {
                        }
                    }
                }, 1500); // ✅ REDUCED: From 3000ms to 1500ms
            } else {
                // Standard timeout for normal sessions
                setTimeout(() => {
                    suppressRefetchRef.current = false;
                    syncWithSessionStorage(true);
                }, 150); // ✅ REDUCED: From 300ms to 150ms
            }
        }
    }, [isCallSelected, isChatSelected, currentSessionId, syncWithSessionStorage]);

    // ❌ REMOVE the automatic event dispatching from updateSessionStorage
    const updateSessionStorage = useCallback((
        sessionId: string | number | undefined,
        updateFn: (session: any) => void
    ) => {
        if (!sessionId) return;

        try {
            const storedSessions = localStorage.getItem('customerSupportSession');
            if (!storedSessions) return;

            const sessions = JSON.parse(storedSessions);
            const sessionIndex = sessions.findIndex((s: any) =>
                (typeof sessionId === 'string' && s.callId === sessionId) ||
                (typeof sessionId === 'number' && s.chatSessionId === sessionId)
            );

            if (sessionIndex >= 0) {
                // Get the session and apply the update function
                const session = sessions[sessionIndex];
                updateFn(session);

                // Save the updated sessions back to localStorage
                localStorage.setItem('customerSupportSession', JSON.stringify(sessions));

                // ❌ REMOVE: Don't dispatch event automatically
                // This was causing the infinite loop
                // setTimeout(() => {
                //     window.dispatchEvent(new CustomEvent('minimizedTicketsChanged', {
                //         detail: { sessionId }
                //     }));
                // }, 100);
            }
        } catch (error) {
            console.error('Error updating session storage directly:', error);
        }
    }, []);

    // ❌ MODIFY: Simplified effect to only initialize once
    useEffect(() => {
        // Only initialize once when the hook mounts
        if (!initializedRef.current) {
            initializedRef.current = true;

            // Only do initial sync, no event listeners
            setTimeout(() => {
                syncWithSessionStorage(true);
            }, 500); // Slightly longer delay to ensure everything is ready
        }
    }, []); // Empty dependency array

    // NEW: Direct localStorage purging function specifically for modal deletion
    const purgeModalFromLocalStorage = useCallback((
        sessionId: string | number | undefined,
        modalId: number
    ) => {
        if (!sessionId) return;

        try {
            const storedSessions = localStorage.getItem('customerSupportSession');
            if (!storedSessions) return;

            const sessions = JSON.parse(storedSessions);
            const sessionIndex = sessions.findIndex((s: any) =>
                (typeof sessionId === 'string' && s.callId === sessionId) ||
                (typeof sessionId === 'number' && s.chatSessionId === sessionId)
            );

            if (sessionIndex >= 0) {
                const session = sessions[sessionIndex];
                let modalRemoved = false;

                // Check every tab in the session
                if (session.searchData) {
                    session.searchData.forEach((tab: any) => {
                        // Remove from minimizedModals array
                        if (tab.minimizedModals) {
                            const initialLength = tab.minimizedModals.length;
                            tab.minimizedModals = tab.minimizedModals.filter((id: number) => id !== modalId);
                            if (tab.minimizedModals.length < initialLength) {
                                modalRemoved = true;
                            }
                        }

                        // Remove from formData if exists
                        if (tab.formData && tab.formData[modalId]) {
                            delete tab.formData[modalId];
                            modalRemoved = true;
                        }
                    });
                }

                // Only save if we actually removed something
                if (modalRemoved) {
                    localStorage.setItem('customerSupportSession', JSON.stringify(sessions));
                    return true;
                } else {
                }
            }
        } catch (error) {
            console.error('Error purging modal from localStorage:', error);
        }

        return false;
    }, []);

    // Handle minimizing a modal - SIMPLIFIED
    const handleMinimizeModal = useCallback((
        selectedServiceTypeId: number,
        formRef: any,
        serviceName: string = 'Unnamed Service',
        tabKey?: string,
        tabs?: TabData[],
        setTabs?: React.Dispatch<React.SetStateAction<TabData[]>>
    ) => {
        if (selectedServiceTypeId !== null && formRef.current) {
            const formValues = formRef.current.form.getFieldsValue();
            const sessionId = getActiveSessionId();

            // Prepare the modal data
            const modalData = {
                ...formValues,
                serviceTypeId: selectedServiceTypeId,
                serviceTypeName: serviceName
            };

            // ✅ ADD: Enable sync updates
            suppressRefetchRef.current = false;

            // Update local minimized modal state
            setMinimizedModals(prev => [
                ...prev.filter(m => m.id !== selectedServiceTypeId), // ✅ ADD: Remove existing if any
                {
                    id: selectedServiceTypeId,
                    name: serviceName,
                    formData: modalData
                }
            ]);

            // If in DetailView mode with tabs
            if (tabKey && tabs && setTabs) {
                // Update tab data
                const updatedTabs = tabs.map(tab => {
                    if (tab.key === tabKey) {
                        // Get existing minimized modals and form data
                        const existingModals = tab.minimizedModals || [];
                        const existingFormData = tab.formData || {};

                        // Create a new array with the new ID
                        const newMinimizedModals = [...existingModals];
                        if (!newMinimizedModals.includes(selectedServiceTypeId)) {
                            newMinimizedModals.push(selectedServiceTypeId);
                        }

                        return {
                            ...tab,
                            minimizedModals: newMinimizedModals,
                            formData: {
                                ...existingFormData,
                                [selectedServiceTypeId]: modalData
                            }
                        };
                    }
                    return tab;
                });

                setTabs(updatedTabs);

                // Save via saveSessionTabs if available
                if (sessionId && saveSessionTabs) {
                    saveSessionTabs({
                        ...(typeof sessionId === 'string'
                            ? { callId: sessionId }
                            : { chatSessionId: sessionId }),
                        searchData: updatedTabs
                    });
                }
            } else {
                // For lookup view - update UC tab
                if (sessionId) {
                    updateSessionStorage(sessionId, (session) => {
                        // Find the current active tab if tabKey is provided
                        let targetTab;

                        if (tabKey) {
                            // If tabKey is provided, use that specific tab
                            targetTab = session.searchData?.find((tab: any) => tab.key === tabKey);
                        }

                        // If no tabKey or tab not found, fall back to first UC tab
                        if (!targetTab) {
                            targetTab = session.searchData?.find((tab: any) => tab.key.startsWith('UC-'));
                        }

                        if (targetTab) {
                            // Update existing tab
                            targetTab.minimizedModals = [...(targetTab.minimizedModals || [])];
                            if (!targetTab.minimizedModals.includes(selectedServiceTypeId)) {
                                targetTab.minimizedModals.push(selectedServiceTypeId);
                            }

                            targetTab.formData = {
                                ...(targetTab.formData || {}),
                                [selectedServiceTypeId]: modalData
                            };
                        } else {
                            // ✅ ENHANCED: Create new UC tab with session search criteria
                            const sessionSearchCriteria = (() => {
                                try {
                                    const storedSessions = localStorage.getItem('customerSupportSession');
                                    if (!storedSessions) return {};

                                    const sessions = JSON.parse(storedSessions);
                                    const currentSession = sessions.find((s: any) =>
                                        (typeof sessionId === 'string' && s.callId === sessionId) ||
                                        (typeof sessionId === 'number' && s.chatSessionId === sessionId)
                                    );

                                    return currentSession?.globalSearchCriteria || {};
                                } catch (error) {
                                    console.error('Error getting session search criteria:', error);
                                    return {};
                                }
                            })();

                            const newUCTab = {
                                label: "Kết quả tìm kiếm",
                                key: `UC-${Date.now()}`,
                                customerId: 0,
                                customerCode: '',
                                minimizedModals: [selectedServiceTypeId],
                                formData: {
                                    [selectedServiceTypeId]: modalData
                                },
                                searchCriteria: sessionSearchCriteria, // ✅ ADD: Include session search criteria
                                tabType: 'SEARCH'
                            };

                            session.searchData = [...(session.searchData || []), newUCTab];
                        }
                    });
                }
            }
        }
    }, [getActiveSessionId, saveSessionTabs, updateSessionStorage, syncWithSessionStorage]);

    // Delete minimized modal - ENHANCED
    const handleDeleteMinimizedModal = useCallback((
        id: number,
        tabKey?: string,
        tabs?: TabData[],
        setTabs?: React.Dispatch<React.SetStateAction<TabData[]>>
    ) => {
        // Prevent multiple deletions at once
        if (deleteInProgressRef.current) {
            return;
        }

        deleteInProgressRef.current = true;

        try {
            // ✅ ADD: Enable sync updates
            suppressRefetchRef.current = false;

            // Update local state immediately
            setMinimizedModals(prev => prev.filter(modal => modal.id !== id));

            // Get session ID
            const sessionId = getActiveSessionId();

            // Update tabs if in detail view
            if (tabKey && tabs && setTabs) {
                const updatedTabs = tabs.map(tab => {
                    if (tab.key === tabKey) {
                        // Filter out the modal from minimizedModals
                        const updatedMinimizedModals = (tab.minimizedModals || [])
                            .filter(modalId => modalId !== id);

                        // Create a new formData without this modal
                        const updatedFormData = { ...tab.formData };
                        delete updatedFormData[id];

                        return {
                            ...tab,
                            minimizedModals: updatedMinimizedModals,
                            formData: updatedFormData
                        };
                    }
                    return tab;
                });

                setTabs(updatedTabs);

                // Save updated tabs to session storage
                if (sessionId && saveSessionTabs) {
                    saveSessionTabs({
                        ...(typeof sessionId === 'string'
                            ? { callId: sessionId }
                            : { chatSessionId: sessionId }),
                        searchData: updatedTabs
                    });
                }
            }

            // ENHANCED: Always directly purge from localStorage to ensure complete removal
            if (sessionId) {
                // First attempt direct update using updateSessionStorage
                updateSessionStorage(sessionId, (session) => {
                    // Check only the target tab (or all if tabKey is not provided)
                    if (session.searchData) {
                        for (let i = 0; i < session.searchData.length; i++) {
                            const tab = session.searchData[i];

                            // If tabKey specified, process only that tab
                            if (tabKey && tab.key !== tabKey) {
                                continue;
                            }

                            // If this tab has this modal minimized
                            if (tab.minimizedModals?.includes(id)) {
                                // Remove from minimizedModals array
                                tab.minimizedModals = tab.minimizedModals.filter((modalId: number) => modalId !== id);

                                // Remove from formData if exists
                                if (tab.formData && tab.formData[id]) {
                                    delete tab.formData[id];
                                }
                            }
                        }
                    }
                });

                // Then as a guaranteed backup, use direct purge method **only** when no tabKey is specified.
                // This prevents removal of modals in other tabs that legitimately share the same serviceTypeId.
                if (!tabKey) {
                    setTimeout(() => {
                        const purged = purgeModalFromLocalStorage(sessionId, id);
                        if (purged) {
                            // console.log(`🗑️ Modal ID ${id} purged as backup measure`);
                        }
                    }, 100);
                }
            }
        } catch (error) {
            console.error('Error deleting minimized modal:', error);
        } finally {
            // Reset deletion flag
            setTimeout(() => {
                deleteInProgressRef.current = false;
            }, 150); // ✅ REDUCED: From 300ms to 150ms
        }
    }, [getActiveSessionId, saveSessionTabs, updateSessionStorage, purgeModalFromLocalStorage, syncWithSessionStorage]);

    // Restore minimized modal - ENHANCED
    const handleRestoreModal = useCallback((
        id: number,
        tabKey?: string,
        tabs?: TabData[],
        setTabs?: React.Dispatch<React.SetStateAction<TabData[]>>,
        formRef?: any
    ): MinimizedModal | null => {
        // Find the modal to restore
        const modalToRestore = minimizedModals.find(modal => modal.id === id);
        if (!modalToRestore) {
            console.error(`Modal with ID ${id} not found`);
            return null;
        }

        // Do NOT remove from minimizedModals or localStorage when restoring
        return modalToRestore;
    }, [minimizedModals]);

    return {
        minimizedModals,
        setMinimizedModals,
        handleMinimizeModal,
        handleDeleteMinimizedModal,
        handleRestoreModal,
        initializeMinimizedModals,
        syncWithSessionStorage
    };
};
